import React, { forwardRef, useImperativeHandle,  useRef, useState } from  "react";
import { RCCAlert, RCCButton, RCCCheckBox,  RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import LovContractItems from "src/components/Lov/ContractItems";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Select from "src/components/Select";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IComponentCommercialPlansModals, IEftbd001, IEftbd002, IRefCommercialPlansModals } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefCommercialPlansModals, IComponentCommercialPlansModals> = ({
	paginationRef,
	openModalRevalidate
}, ref) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const modalDelete = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register, 
		control, 
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors,
		setValue
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const [isAdd, setIsAdd] = useState(false);
	const [selectedBussiness, setSelectedBussiness] = useState<IEftbd001>({} as IEftbd001);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const queryData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "CommercialPlans/Select",
				urlParameters : {
					id
				}
			});
			setSelectedBussiness(data);
			appendItems(data.eftbd002 ?? []);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const submitAddOrChange = async (data: any) => {
		clearErrors();
		clearErrors("eftbd002");
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();

			const objToInt = (obj: any) => {
				if(typeof obj === "object")
					return parseInt(obj.value, 10);
				if(typeof obj === "string" && obj !== "")
					return parseInt(obj, 10);
				if(typeof obj === "string" && obj === "")
					return undefined;
				if(typeof obj !== "number")
					return undefined;
				return obj;
			};
			
			const dataChanged = data;
			dataChanged.efd001Duracao = objToInt(data.efd001Duracao) ?? 0;
			dataChanged.efd001Tipo = objToInt(data.efd001Tipo);
			dataChanged.efd001Ativo = data.efd001Ativo ? "S" : "N";
			dataChanged.eftbd002 = data.eftbd002 && data.eftbd002.length > 0 ? data.eftbd002 : undefined;

			if(data.eftbd002)
				data.eftbd002.forEach((item: any) => {
					const eftbd002Changed = item;
					eftbd002Changed.efd002Tipo = item.efd002Tipo ?? undefined;
					eftbd002Changed.efd002Valor = item.efd002Valor ? parseInt(item.efd002Valor,10) : undefined;
					eftbd002Changed.efd002BloqExcedido = item.efd002BloqExcedido ? "S": "N";
				});
	
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "CommercialPlans/Insert",
					body: {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "CommercialPlans/Update",
					body: {
						...data,
						efd001Id: selectedBussiness.efd001Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Plano comercial adicionado com sucesso") : translate("Plano comercial alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(!isAdd)
				openModalRevalidate(selectedBussiness.efd001Id);

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){
			modalAddOrChange.current?.resetLoadingButton();

			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar plano comercial") : translate("Erro ao alterar plano comercial"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efd001DescricaoConfirmation": selectedBussiness.efd001Descricao},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "CommercialPlans/Delete",
				urlParameters: {
					id: selectedBussiness.efd001Id
				}
			});

			addToast({
				color: "green",
				description: translate("Plano comercial excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir plano comercial"), addToast);
		}	
	};

	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelectedBussiness({} as IEftbd001);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IEftbd001) => {
		reset();
		setIsAdd(false);
		queryData(row.efd001Id);
	};

	const actionButtonDelete = (row: IEftbd001) => {
		resetDelete(); 
		setSelectedBussiness(row); 
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const { 
		fields: fieldsItems, 
		remove: removeItems,
		append: appendItems 
	} = useFieldArray({
		control,
		name: "eftbd002"
	});
    

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de Plano Comercial")}
				description= {`${translate("Deseja realmente excluir o plano comercial")}: ${selectedBussiness.efd001Descricao}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efd001Descricao"
						label={translate("Digite o nome do plano comercial para excluí-lo")}
						maxLength={200}
						error={errorsDelete.efd001Descricao}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Plano Comercial") : translate("Alterar Plano Comercial"),
					subtitle: !isAdd ? `${translate("Plano Comercial")}: ${selectedBussiness.efd001Descricao}`: "",
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs 
					name="TabsCommercialPlans"
					tabInit={0}
					options={[{
						label: translate("Dados Gerais"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									alignItems="center"
									justify="flex-end"
								>
									<Audit data={selectedBussiness} type="pop"/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={register}
										name="efd001Descricao"
										label={translate("Descrição")}
										maxLength={200}
										defaultValue={selectedBussiness.efd001Descricao ?? ""}
										error={errors.efd001Descricao}
										required
									/>
								</RCCGrid>
								<RCCGrid 
									item 
									container
									xs={12}
									sm={10}
									style={{marginTop: "10px"}}
								>
									<Select 
										control={control}
										register={register}
										label={translate("Tipo")}
										name="efd001Tipo"
										error={errors.efd001Tipo} 
										defaultValue={selectedBussiness.efd001Tipo ? String(selectedBussiness.efd001Tipo):  "1"}
										options={[
											{value: "1", label: translate("Normal")},
											{value: "2", label: translate("Interno")}
										]}	
										required					
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={register}
										name="efd001Duracao"
										label={translate("Duração (dias) do contrato")}
										type="number"
										maxLength={5}
										min={0}
										defaultValue={selectedBussiness.efd001Duracao ?? 365}
										error={errors.efd001Duracao}
									/>
								</RCCGrid>
								<RCCGrid 
									item 
									container
									xs={12}
									sm={10}
									style={{marginTop: "10px"}}
								>
									<RCCSwitch 
										label={translate("Ativo")} 
										name="efd001Ativo" 
										defaultChecked={selectedBussiness.efd001Ativo === "S" || isAdd} 
										error={errors.efd001Ativo}
										register={register}/>
								</RCCGrid>
							</RCCGrid>
						)
					},{
						label: translate("Itens do Plano"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid 
									xs={12}
									sm={10}
									item
									container 
									alignItems="flex-end" 
									direction="column"
								>	
									<RCCTooltip
										text={translate("Adicionar Item")}
										color="purple"	
									>
										<RCCButton
											icon={{name: "plus", color: "#FFF", size: 18}}
											color="purple"
											layout="isRounded"
											onClick={() => {
												clearErrors("eftbd002");
												appendItems({} as IEftbd002); 
											}}
											size="md"
										/>
									</RCCTooltip>
								</RCCGrid>	
								<RCCGrid 
									xs={12}
									sm={10}
									container 
									item
								>
									{errors.eftbd002 && errors.eftbd002.message && (
										<Title color="red" size="xs">
											<RCCIcon
												name="infoCircle"
												color="var(--red)"
												style={{ marginRight: "8px"}}
											/>
											{errors.eftbd002.message}
										</Title>
									)}
									{fieldsItems.length <= 0 && (
										<RCCAlert 
											description={translate("Não há nenhum item cadastrado para este plano")}
										/>
									)}
								</RCCGrid>
								<RCCGrid 
									xs={12}
									sm={10}
									item
									container 
								>
									{fieldsItems.map((field: any, index) => (
										<RCCGrid
											container
											item
											justifyContent="space-between"
											alignContent="center"
											alignItems="center"
											key={field.id}
											spacing={1}
										>
											<RCCGrid 
												item 
												md={5}
												sm={6}
												xs={6}
											>
												<LovContractItems 
													control={control}
													name={`eftbd002.${index}.efd002Tipo`}
													label={translate("Tipo")}
													defaultValue={field.efd002Tipo ? {
														value: field.efd002Tipo,
														label: field.efa016Descricao
													} : ""}	
													onChange={(e: any) => {
														setValue(`eftbd002.${index}.efd002Tipo`, e.value);
														clearErrors("eftbd002");
													}}
													error={errors.eftbd002 && errors?.eftbd002[index]?.efd002Tipo}
												/>
											</RCCGrid>
											<RCCGrid 
												item 
												md={3}
												sm={6}
												xs={6}
											>
												<RCCInput	
													name={`eftbd002.${index}.efd002Valor`}
													label={translate("Valor")}
													register={register}	
													size={10}
													type="number"
													defaultValue={field.efd002Valor}	
													error={errors.eftbd002 && errors?.eftbd002[index]?.efd002Valor}
												/>
											</RCCGrid>
											<RCCGrid 
												item 
												container
												md={4}
												sm={12}
												xs={12}
												justifyContent="space-between"
												alignItems="flex-end"
											>
												<RCCCheckBox 
													name={`eftbd002.${index}.efd002BloqExcedido`}
													label={translate("Bloquear Excedido")}
													defaultChecked={field.efd002Bloqexcedido === "S"}	
													register={register}		
													error={errors.eftbd002 && errors?.eftbd002[index]?.efd002BloqExcedido}	
												/>
												<RCCButton
													icon={{name: "trash", color: "#FFF"}}
													color="red"
													layout="isRounded"
													onClick={() => { 
														clearErrors("eftbd002");
														removeItems(index); 
													}}
													size="md"
													style={{ maxWidth: "35px" }}
												/>
											</RCCGrid>
										</RCCGrid>
									))}
								</RCCGrid>
								
							</RCCGrid>
						)
					}]}
				/>
			
			</Modal>
		</>
	);
};


export default forwardRef(Modals);
